// @ts-nocheck
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { MenuItem, Button, Intent } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { debounce } from 'lodash';

import { FormattedMessage as T } from '@/components';
import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';
import intl from 'react-intl-universal';
import { useSearchableContacts, useContact } from '@/hooks/query';

export function ContactsSearchableSuggestField({
  initialContactId,
  selectedContactId,
  defaultTextSelect = intl.get('select_contact'),
  onContactSelected,

  selectedContactType = [],
  popoverFill = false,

  ...suggestProps
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce the search query
  const debouncedSetSearch = useRef(
    debounce((query) => {
      setDebouncedSearchQuery(query);
    }, 300)
  );

  useEffect(() => {
    debouncedSetSearch.current(searchQuery);
  }, [searchQuery]);

  // Fetch contacts based on search query
  const { data: contactsList = [], isLoading } = useSearchableContacts(
    debouncedSearchQuery,
    {
      keepPreviousData: true,
    }
  );

  // Fetch the specific selected contact if it's not in the search results
  // This ensures the contact is loaded when editing an existing entry
  const { data: selectedContactData } = useContact(
    selectedContactId,
    {
      enabled: !!selectedContactId && !contactsList.find((c) => c.id === selectedContactId),
    }
  );

  // Transform contacts with unique IDs and include the selected contact if fetched separately
  const contacts = useMemo(() => {
    const allContacts = [...contactsList];

    // Add the selected contact if it was fetched separately and not already in the list
    if (selectedContactData && !allContacts.find((c) => c.id === selectedContactId)) {
      allContacts.unshift(selectedContactData);
    }

    return allContacts.map((contact) => ({
      ...contact,
      _id: `${contact.id}_${contact.contact_type}`,
    }));
  }, [contactsList, selectedContactData, selectedContactId]);

  const initialContact = useMemo(
    () => contacts.find((a) => a.id === initialContactId),
    [initialContactId, contacts],
  );

  const [selectedContact, setSelectedContact] = useState(
    initialContact || null,
  );

  useEffect(() => {
    if (typeof selectedContactId !== 'undefined') {
      const contact = selectedContactId
        ? contacts.find((a) => a.id === selectedContactId)
        : null;
      setSelectedContact(contact);
    }
  }, [selectedContactId, contacts, setSelectedContact]);

  const contactRenderer = useCallback(
    (contact, { handleClick }) => (
      <MenuItem
        key={contact.id}
        text={contact.display_name}
        label={contact.formatted_contact_service}
        onClick={handleClick}
      />
    ),
    [],
  );

  const onContactSelect = useCallback(
    (contact) => {
      setSelectedContact({ ...contact });
      onContactSelected && onContactSelected(contact);
    },
    [setSelectedContact, onContactSelected],
  );

  // Handle clearing the contact selection
  const handleClearContact = useCallback(
    (e) => {
      e.stopPropagation();
      setSelectedContact(null);
      setSearchQuery('');
      // Call onContactSelected with null to notify parent component
      onContactSelected && onContactSelected(null);
    },
    [setSelectedContact, onContactSelected],
  );

  const handleInputValueRenderer = (inputValue) => {
    if (inputValue) {
      return inputValue.display_name.toString();
    }
  };

  // Handle query change for server-side search
  const handleQueryChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  // Client-side filter is minimal since we're doing server-side search
  const filterContacts = useCallback(() => {
    // Return true for all items since filtering is done server-side
    return true;
  }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
      {selectedContact && (
        <Button
          icon="cross"
          minimal
          small
          intent={Intent.NONE}
          onClick={handleClearContact}
          style={{
            flexShrink: 0,
            padding: '3px',
            height: '24px',
            minHeight: '24px',
            minWidth: '24px',
            width: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Clear selection"
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Suggest
          items={contacts}
          noResults={
            <MenuItem
              disabled={true}
              text={isLoading ? <T id={'loading'} /> : <T id={'no_results'} />}
            />
          }
          itemRenderer={contactRenderer}
          itemPredicate={filterContacts}
          onItemSelect={onContactSelect}
          selectedItem={selectedContact}
          inputProps={{
            placeholder: defaultTextSelect,
          }}
          resetOnClose={true}
          popoverProps={{ minimal: true, boundary: 'window' }}
          inputValueRenderer={handleInputValueRenderer}
          onQueryChange={handleQueryChange}
          query={searchQuery}
          className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
            [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
          })}
          {...suggestProps}
        />
      </div>
    </div>
  );
}
